/**
 * Filter + selection state synced to URL search params.
 */

import { create } from "zustand";
import type { ScoreBucket, Category, SkillStatus } from "../lib/types";
import { defaultFacetFilters, getFacetFilterIds } from "../lib/plugin-facet-filters";

export type SortKey = "score-asc" | "score-desc" | "name-asc" | "lines-desc" | "recent";
export type TrendKey = "avg" | "above85" | "below70" | "broken";
export type TrendSource = "portfolio" | "autoresearch";

interface FilterState {
  search: string;
  sort: SortKey;
  selected: string | null;
  compare: [string | null, string | null];
  buckets: Record<ScoreBucket, boolean>;
  statuses: Record<SkillStatus, boolean>;
  categories: Record<Category, boolean>;
  structures: { router: boolean; refs: boolean; broken: boolean };
  facetFilters: Record<string, boolean>;
  minLines: number;
  trend: TrendKey;
  trendSource: TrendSource;
  setSearch: (q: string) => void;
  setSort: (s: SortKey) => void;
  select: (name: string | null) => void;
  toggleCompare: (name: string) => void;
  clearCompare: () => void;
  toggleBucket: (b: ScoreBucket) => void;
  toggleStatus: (s: SkillStatus) => void;
  toggleCategory: (c: Category) => void;
  toggleStructure: (k: "router" | "refs" | "broken") => void;
  toggleFacetFilter: (id: string) => void;
  setMinLines: (n: number) => void;
  setTrend: (t: TrendKey) => void;
  setTrendSource: (s: TrendSource) => void;
  reset: () => void;
  hydrateFromUrl: () => void;
  applyPreset: (preset: "all" | "below85" | "critical" | "broken") => void;
}

const defaults = {
  search: "",
  sort: "score-desc" as SortKey,
  selected: null as string | null,
  compare: [null, null] as [string | null, string | null],
  buckets: { excellent: true, good: true, "needs-work": true, poor: true } as Record<ScoreBucket, boolean>,
  statuses: { healthy: true, warning: true, critical: true } as Record<SkillStatus, boolean>,
  categories: { agent: true, skill: true, utrecht: true, workflow: true, other: true } as Record<Category, boolean>,
  structures: { router: false, refs: false, broken: false },
  facetFilters: defaultFacetFilters(),
  minLines: 0,
  trend: "avg" as TrendKey,
  trendSource: "portfolio" as TrendSource,
};

const BUCKET_KEYS = ["excellent", "good", "needs-work", "poor"] as const;
const STATUS_KEYS = ["healthy", "warning", "critical"] as const;
const CAT_KEYS = ["agent", "skill", "utrecht", "workflow", "other"] as const;

function parseBoolList<T extends string>(raw: string | null, keys: readonly T[], fallback: Record<T, boolean>): Record<T, boolean> {
  if (!raw) return { ...fallback };
  const set = new Set(raw.split(",").filter(Boolean));
  const out = {} as Record<T, boolean>;
  for (const k of keys) out[k] = set.size === 0 ? fallback[k] : set.has(k);
  return out;
}

function encodeBoolList<T extends string>(map: Record<T, boolean>, keys: readonly T[]): string {
  return keys.filter((k) => map[k]).join(",");
}

function filterSearchParams(): URLSearchParams {
  const p = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  const qIdx = hash.indexOf("?");
  if (qIdx >= 0) {
    const legacy = new URLSearchParams(hash.slice(qIdx + 1));
    for (const [k, v] of legacy) if (!p.has(k)) p.set(k, v);
  }
  return p;
}

function routeHash(): string {
  const base = window.location.hash.replace(/^#\/?/, "").split("?")[0];
  if (!base || base === "monitor") return "#";
  return `#/${base}`;
}

function readUrl(): Partial<typeof defaults> {
  const p = filterSearchParams();
  const patch: Partial<typeof defaults> = {};
  if (p.has("q")) patch.search = p.get("q") ?? "";
  const sort = p.get("sort");
  if (sort && ["score-asc", "score-desc", "name-asc", "lines-desc", "recent"].includes(sort)) {
    patch.sort = sort as SortKey;
  }
  if (p.has("skill")) patch.selected = p.get("skill");
  const min = p.get("minLines");
  if (min !== null && min !== "") patch.minLines = Math.max(0, parseInt(min, 10) || 0);
  if (p.has("tiers")) patch.buckets = parseBoolList(p.get("tiers"), BUCKET_KEYS, defaults.buckets);
  if (p.has("statuses")) patch.statuses = parseBoolList(p.get("statuses"), STATUS_KEYS, defaults.statuses);
  if (p.has("cats")) patch.categories = parseBoolList(p.get("cats"), CAT_KEYS, defaults.categories);
  const struct = p.get("struct");
  if (struct) {
    const set = new Set(struct.split(","));
    patch.structures = {
      router: set.has("router"),
      refs: set.has("refs"),
      broken: set.has("broken"),
    };
  }
  const trend = p.get("trend");
  if (trend && ["avg", "above85", "below70", "broken"].includes(trend)) patch.trend = trend as TrendKey;
  const ts = p.get("trendSrc");
  if (ts === "portfolio" || ts === "autoresearch") patch.trendSource = ts;
  const cmp = p.get("compare");
  if (cmp) {
    const [a, b] = cmp.split(",");
    patch.compare = [a || null, b || null];
  }
  const facets = p.get("facets");
  if (facets) {
    const set = new Set(facets.split(",").filter(Boolean));
    const ff = defaultFacetFilters();
    for (const id of getFacetFilterIds()) ff[id] = set.has(id);
    patch.facetFilters = ff;
  }
  return patch;
}

function writeUrl(state: FilterState) {
  const p = new URLSearchParams();
  if (state.search) p.set("q", state.search);
  if (state.sort !== defaults.sort) p.set("sort", state.sort);
  if (state.selected) p.set("skill", state.selected);
  if (state.minLines > 0) p.set("minLines", String(state.minLines));
  const tiers = encodeBoolList(state.buckets, BUCKET_KEYS);
  if (tiers !== encodeBoolList(defaults.buckets, BUCKET_KEYS)) p.set("tiers", tiers);
  const statuses = encodeBoolList(state.statuses, STATUS_KEYS);
  if (statuses !== encodeBoolList(defaults.statuses, STATUS_KEYS)) p.set("statuses", statuses);
  const cats = encodeBoolList(state.categories, CAT_KEYS);
  if (cats !== encodeBoolList(defaults.categories, CAT_KEYS)) p.set("cats", cats);
  const structParts: string[] = [];
  if (state.structures.router) structParts.push("router");
  if (state.structures.refs) structParts.push("refs");
  if (state.structures.broken) structParts.push("broken");
  if (structParts.length) p.set("struct", structParts.join(","));
  if (state.trend !== defaults.trend) p.set("trend", state.trend);
  if (state.trendSource !== defaults.trendSource) p.set("trendSrc", state.trendSource);
  const [a, b] = state.compare;
  if (a || b) p.set("compare", [a, b].filter(Boolean).join(","));
  const activeFacets = getFacetFilterIds().filter((id) => state.facetFilters[id]);
  if (activeFacets.length) p.set("facets", activeFacets.join(","));
  const qs = p.toString();
  const url = `${window.location.pathname}${qs ? `?${qs}` : ""}${routeHash()}`;
  window.history.replaceState(null, "", url);
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleUrlSync(getState: () => FilterState) {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => writeUrl(getState()), 120);
}

export const useFilters = create<FilterState>((set, get) => ({
  ...defaults,
  ...readUrl(),
  hydrateFromUrl: () => set({ ...defaults, ...readUrl() }),
  setSearch: (search) => { set({ search }); scheduleUrlSync(get); },
  setSort: (sort) => { set({ sort }); scheduleUrlSync(get); },
  select: (selected) => { set({ selected }); scheduleUrlSync(get); },
  toggleCompare: (name) => {
    set((s) => {
      const [a, b] = s.compare;
      if (a === name || b === name) return { compare: [a === name ? null : a, b === name ? null : b] };
      if (!a) return { compare: [name, b] };
      if (!b) return { compare: [a, name] };
      return { compare: [name, b] };
    });
    scheduleUrlSync(get);
  },
  clearCompare: () => { set({ compare: [null, null] }); scheduleUrlSync(get); },
  toggleBucket: (b) => { set((s) => ({ buckets: { ...s.buckets, [b]: !s.buckets[b] } })); scheduleUrlSync(get); },
  toggleStatus: (st) => { set((s) => ({ statuses: { ...s.statuses, [st]: !s.statuses[st] } })); scheduleUrlSync(get); },
  toggleCategory: (c) => { set((s) => ({ categories: { ...s.categories, [c]: !s.categories[c] } })); scheduleUrlSync(get); },
  toggleStructure: (k) => { set((s) => ({ structures: { ...s.structures, [k]: !s.structures[k] } })); scheduleUrlSync(get); },
  toggleFacetFilter: (id) => {
    set((s) => ({ facetFilters: { ...s.facetFilters, [id]: !s.facetFilters[id] } }));
    scheduleUrlSync(get);
  },
  setMinLines: (minLines) => { set({ minLines }); scheduleUrlSync(get); },
  setTrend: (trend) => { set({ trend }); scheduleUrlSync(get); },
  setTrendSource: (trendSource) => { set({ trendSource }); scheduleUrlSync(get); },
  reset: () => { set(defaults); scheduleUrlSync(get); },
  applyPreset: (preset) => {
    if (preset === "all") {
      set({ ...defaults });
    } else if (preset === "below85") {
      set({
        buckets: { excellent: false, good: false, "needs-work": true, poor: true },
        statuses: { healthy: true, warning: true, critical: true },
      });
    } else if (preset === "critical") {
      set({
        statuses: { healthy: false, warning: false, critical: true },
      });
    } else if (preset === "broken") {
      set({ structures: { router: false, refs: false, broken: true } });
    }
    scheduleUrlSync(get);
  },
}));

if (typeof window !== "undefined") {
  useFilters.getState().hydrateFromUrl();
  window.addEventListener("popstate", () => useFilters.getState().hydrateFromUrl());
  window.addEventListener("filters-updated", () => scheduleUrlSync(() => useFilters.getState()));
}